import { useLeagueFormat } from "@app/league/hooks/use-league-format.hook";
import { buildLeagueHomeModel } from "@app/league/utils/league-home-card";
import { nextTargetText } from "@app/league/utils/league-next-target";
import type { LeagueSummary } from "@core/league/types";
import { URLS } from "@core/shared/utils";
import { platformFeaturesAtom } from "@framework/shared/atoms";
import { trans, plainTrans } from "@mongez/localization";
import { Link } from "@remix-run/react";
import { ArrowRight, Sparkles, Trophy } from "lucide-react";

/**
 * Home card (`?view=card`): month rank headline, this week's zone and the
 * next target. Renders nothing when League is off for this channel.
 *
 * @example
 * <LeagueHomeCard data={leagueHome} />
 */
export function LeagueHomeCard({ data }: { data?: LeagueSummary | null }) {
  const features = platformFeaturesAtom.useValue();
  const { num, points } = useLeagueFormat();
  const model = buildLeagueHomeModel(data, features.league === true);

  if (model.kind === "hidden") return null;

  const target =
    model.kind === "ranked" ? nextTargetText(model.nextTarget) : null;
  const headline =
    model.kind === "ranked" && model.monthRank !== null
      ? trans("league.home.monthRank", {
          rank: num(model.monthRank),
          total: num(model.monthParticipants),
        })
      : model.kind === "noGrade"
        ? trans("league.noGrade.title")
        : model.kind === "closing"
          ? trans("league.week.closing.title")
          : trans("league.start.title");

  return (
    <section
      aria-label={plainTrans("league.title")}
      className="relative overflow-hidden rounded-3xl border-b-4 border-primary-950/30 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-500 p-5 text-primary-foreground shadow-sm sm:p-6"
    >
      <div
        aria-hidden="true"
        className="absolute -end-12 -top-16 h-44 w-44 rounded-full bg-white/10 blur-3xl"
      />
      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/15">
            {model.kind === "start" ? (
              <Sparkles aria-hidden="true" className="h-6 w-6 text-amber-200" />
            ) : (
              <Trophy aria-hidden="true" className="h-6 w-6 text-amber-200" />
            )}
          </span>
          <div className="min-w-0">
            <p className="text-xs font-black uppercase tracking-wider text-primary-100">
              {trans("league.title")}
            </p>
            <h2 className="text-xl font-black leading-snug sm:text-2xl">
              {headline}
            </h2>
            {model.kind === "ranked" ? (
              <div className="mt-2 flex flex-wrap gap-2 text-xs font-black">
                {model.zone ? (
                  <span className="rounded-full bg-white/20 px-3 py-1">
                    {trans(`league.zones.in.${model.zone}`)}
                  </span>
                ) : null}
                {model.pendingReveals > 0 ? (
                  <span className="rounded-full bg-amber-300 px-3 py-1 text-amber-950">
                    {trans("league.home.newResult")}
                  </span>
                ) : null}
              </div>
            ) : null}
            {target ? (
              <p className="mt-2 text-sm font-bold text-primary-50">
                {trans(target.key, {
                  points: points(target.params.points),
                  name: target.params.name,
                })}
              </p>
            ) : null}
          </div>
        </div>
        <Link
          className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-black text-primary-800 shadow-sm hover:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-700"
          to={URLS.league}
        >
          {trans("league.home.open")}
          <ArrowRight aria-hidden="true" className="h-4 w-4 rtl:rotate-180" />
        </Link>
      </div>
    </section>
  );
}
